import { Component, ElementRef, EventEmitter, forwardRef, HostBinding, HostListener, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { BANK_SELECT_COPY } from '../../constants/credit-line.constants';
import { BankOption } from '../../constants/sudeban-banks';

let nextId = 0;

const PANEL_MIN_WIDTH = 320;
const PANEL_MAX_HEIGHT = 280;
const PANEL_GAP = 6;
const VIEWPORT_MARGIN = 8;

/** Sin acentos y en minúsculas: «agrícola» encuentra «Agrícola». */
const normalize = (s: string): string =>
    s
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim();

/**
 * Selector de banco con búsqueda (FINA-3378). Único para el banco desde el que paga el comercio
 * (pago de cuota, abono, modal de bloqueo) y el banco donde recibe el préstamo (solicitud de la
 * línea). Lista propia con el estilo del modal, no la del navegador; al escribir filtra por código
 * SUDEBAN o por nombre (sin acentos). Teclado: ↑/↓, Enter, Escape y Tab.
 *
 * Es un `ControlValueAccessor`: se usa con `ngModel`. El valor es el `code` (por defecto) o el
 * `name` (`valueKey="name"`, la solicitud guarda el nombre). `blurred` reemplaza al `(blur)` del
 * `<select>`. `accent` sigue al acento de quien lo usa: cian en la línea v2, morado en el v1.
 *
 * La lista va en `position: fixed` sobre la ventana (`panelStyle`): los modales tienen scroll propio
 * y la recortarían. Mide al menos `PANEL_MIN_WIDTH`, se abre hacia arriba si abajo no cabe y se
 * recoloca con el scroll y el resize mientras está abierta.
 */
@Component({
    selector: 'fina-bank-select',
    templateUrl: './bank-select.component.html',
    styleUrls: ['./bank-select.component.scss'],
    providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => BankSelectComponent), multi: true }],
})
export class BankSelectComponent implements ControlValueAccessor, OnDestroy {
    @Input() banks: ReadonlyArray<BankOption> = [];
    @Input() valueKey: 'code' | 'name' = 'code';
    @Input() inputId = `fina-bank-select-${++nextId}`;
    @Input() placeholder = BANK_SELECT_COPY.placeholder;
    /** Borde de error (el padre decide cuándo: tocado y vacío). */
    @Input() invalid = false;
    @Input() accent: 'cyan' | 'purple' = 'cyan';

    /** El campo perdió el foco (cerró la lista sin quedarse dentro del componente). */
    @Output() blurred = new EventEmitter<void>();

    @ViewChild('input', { static: true }) inputRef!: ElementRef<HTMLInputElement>;

    readonly copy = BANK_SELECT_COPY;

    open = false;
    /** Posición de la lista sobre la ventana, recalculada al abrir, con scroll y con resize. */
    panelStyle: Record<string, string> = {};
    query = '';
    activeIndex = -1;
    disabled = false;
    value: string | null = null;

    private onChange: (value: string | null) => void = () => {};
    private onTouched: () => void = () => {};

    private readonly reposition = (): void => {
        if (this.open) this.placePanel();
    };

    constructor(private host: ElementRef<HTMLElement>) {}

    ngOnDestroy(): void {
        this.unlisten();
    }

    @HostBinding('class.bank-select--purple') get isPurple(): boolean {
        return this.accent === 'purple';
    }

    get listId(): string {
        return `${this.inputId}-list`;
    }

    /** Banco elegido (por `valueKey`). */
    get selected(): BankOption | null {
        if (this.value == null || this.value === '') return null;
        return this.banks.find(b => this.valueOf(b) === this.value) ?? null;
    }

    /** Texto del campo: lo que se escribe con la lista abierta; el banco elegido con ella cerrada. */
    get inputText(): string {
        if (this.open) return this.query;
        const b = this.selected;
        return b ? this.label(b) : '';
    }

    get filtered(): BankOption[] {
        const q = normalize(this.query);
        if (!q) return [...this.banks];
        const digits = q.replace(/\D/g, '');
        return this.banks.filter(b => normalize(b.name).includes(q) || (!!digits && !!b.code && b.code.includes(digits)));
    }

    label(b: BankOption): string {
        return b.code ? `${b.code} · ${b.name}` : b.name;
    }

    optionId(i: number): string {
        return `${this.inputId}-opt-${i}`;
    }

    trackByName(_: number, b: BankOption): string {
        return b.name;
    }

    // ── ControlValueAccessor ──────────────────────────────────────────────────

    writeValue(value: string | null): void {
        this.value = value ?? null;
    }

    registerOnChange(fn: (value: string | null) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(disabled: boolean): void {
        this.disabled = disabled;
        if (disabled) this.close();
    }

    // ── Interacción ───────────────────────────────────────────────────────────

    openList(): void {
        if (this.disabled || this.open) return;
        this.open = true;
        this.query = '';
        this.placePanel();
        window.addEventListener('scroll', this.reposition, true);
        window.addEventListener('resize', this.reposition);
        const list = this.filtered;
        const current = this.selected;
        this.activeIndex = current ? list.findIndex(b => b.name === current.name) : list.length ? 0 : -1;
    }

    toggle(): void {
        if (this.open) {
            this.close();
            return;
        }
        this.openList();
        this.inputRef.nativeElement.focus();
    }

    onInput(text: string): void {
        if (!this.open) this.openList();
        this.query = text;
        this.activeIndex = this.filtered.length ? 0 : -1;
    }

    onKeydown(event: KeyboardEvent): void {
        const list = this.filtered;
        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                if (!this.open) return this.openList();
                if (list.length) this.activeIndex = (this.activeIndex + 1) % list.length;
                return;
            case 'ArrowUp':
                event.preventDefault();
                if (!this.open) return this.openList();
                if (list.length) this.activeIndex = (this.activeIndex - 1 + list.length) % list.length;
                return;
            case 'Enter':
                if (!this.open) return;
                event.preventDefault();
                if (this.activeIndex >= 0 && list[this.activeIndex]) this.choose(list[this.activeIndex]);
                return;
            case 'Escape':
                if (!this.open) return;
                event.preventDefault();
                event.stopPropagation();
                this.close();
                return;
            case 'Tab':
                this.close();
                return;
        }
    }

    /** `mousedown` (no `click`): elige antes de que el input pierda el foco. */
    onOptionMousedown(event: MouseEvent, bank: BankOption): void {
        event.preventDefault();
        this.choose(bank);
    }

    choose(bank: BankOption): void {
        const value = this.valueOf(bank);
        this.close();
        if (value === this.value) return;
        this.value = value;
        this.onChange(value);
    }

    /** El foco salió del componente (no a la lista ni al botón): cierra y marca tocado. */
    onFocusOut(event: FocusEvent): void {
        const next = event.relatedTarget as Node | null;
        if (next && this.host.nativeElement.contains(next)) return;
        this.close();
        this.onTouched();
        this.blurred.emit();
    }

    @HostListener('document:mousedown', ['$event'])
    onDocumentMousedown(event: MouseEvent): void {
        if (this.open && !this.host.nativeElement.contains(event.target as Node)) this.close();
    }

    private close(): void {
        if (this.open) this.unlisten();
        this.open = false;
        this.query = '';
        this.activeIndex = -1;
    }

    /** Debajo del campo si cabe (o si arriba hay menos sitio); si no, encima. Nunca fuera de la ventana. */
    private placePanel(): void {
        const rect = this.inputRef.nativeElement.getBoundingClientRect();
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const width = Math.min(Math.max(rect.width, PANEL_MIN_WIDTH), vw - VIEWPORT_MARGIN * 2);
        const left = Math.min(Math.max(rect.left, VIEWPORT_MARGIN), vw - width - VIEWPORT_MARGIN);
        const below = vh - rect.bottom - PANEL_GAP - VIEWPORT_MARGIN;
        const above = rect.top - PANEL_GAP - VIEWPORT_MARGIN;
        const down = below >= Math.min(PANEL_MAX_HEIGHT, 200) || below >= above;
        const maxHeight = Math.max(120, Math.min(PANEL_MAX_HEIGHT, down ? below : above));
        this.panelStyle = {
            left: `${left}px`,
            width: `${width}px`,
            'max-height': `${maxHeight}px`,
            ...(down ? { top: `${rect.bottom + PANEL_GAP}px` } : { bottom: `${vh - rect.top + PANEL_GAP}px` }),
        };
    }

    private unlisten(): void {
        window.removeEventListener('scroll', this.reposition, true);
        window.removeEventListener('resize', this.reposition);
    }

    private valueOf(bank: BankOption): string {
        return this.valueKey === 'name' ? bank.name : bank.code ?? bank.name;
    }
}
